import { readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

/**
 * C1 (LRN-4, OPS-1): download a protected API file. A plain `<a href="/api/...">` hits the
 * frontend host and carries no Bearer token; this fetches through apiFetch (API base, token,
 * 401 → refresh → retry from C2) and saves the bytes via a blob URL.
 *
 * Throws an `ApiError` (see `apiErrorText`) when the server refuses. Opens no window (F-6).
 */
export async function downloadApiFile(path: string, suggestedName?: string): Promise<void> {
  const asLink = /[?&]as=link\b/.test(path);
  const res = await apiFetch(path);
  if (!res.ok) throw await readApiErrorResponse(res);
  // H2: a `?as=link` download from Bunny Storage answers `{ data: { url } }` — a signed link that
  // expires in minutes (fetch cannot follow that cross-origin redirect with credentials). A
  // stamped or local PDF still comes back as the file itself.
  if (asLink && (res.headers.get("Content-Type") ?? "").includes("application/json")) {
    const body = (await res.json()) as { data?: { url?: unknown } };
    if (typeof body.data?.url === "string") {
      fileNavigation.assign(body.data.url);
      return;
    }
  }
  const blob = await res.blob();
  const name =
    suggestedName ?? filenameFromDisposition(res.headers.get("Content-Disposition")) ?? "download";
  saveBlob(blob, name);
}

/**
 * S3 F-6 (V-34/W-2): a signed file link is opened in the SAME tab, after the answer. Safari (and
 * Chrome after ~5 s) refuse a new tab opened after an `await`, and pre-opening one inside the
 * click flashed a blank tab for every download answered with the file itself (stamped and local
 * PDFs — most of them). A same-tab navigation is allowed after an await in every browser; the
 * signed URL downloads or shows the PDF, and Back returns to the app. An object so tests can spy.
 */
export const fileNavigation = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

/** Revoking at once makes Safari (and some Chromium builds) abort the save. */
const REVOKE_DELAY_MS = 30_000;

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  // Safari needs the anchor in the document for click() to start a download.
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

/** The file name from a Content-Disposition header (RFC 6266 `filename*` preferred). */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
  const value = plain?.[2] ?? plain?.[1];
  return value ? value.trim() : null;
}
